import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Clock, ArrowRight, CheckCircle } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface WhitePaper {
  id: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  downloadUrl: string;
  coverImage?: string;
  highlights: string[];
}

const whitePapers: WhitePaper[] = [
  {
    id: "b2b-lead-gen-2025",
    title: "The B2B Lead Generation Playbook 2025",
    description: "A comprehensive guide to building predictable B2B pipeline using modern marketing automation, ABM strategies, and data-driven targeting.",
    category: "Lead Generation",
    readTime: "15 min read",
    downloadUrl: "#",
    highlights: [
      "Account-based marketing frameworks",
      "Marketing automation workflows",
      "Lead scoring models that work",
      "ROI calculation templates"
    ]
  },
  {
    id: "enterprise-seo-guide",
    title: "Enterprise SEO: Scaling Organic Growth",
    description: "How established businesses can dominate search results and capture high-intent buyers with strategic SEO positioning.",
    category: "SEO",
    readTime: "20 min read",
    downloadUrl: "#",
    highlights: [
      "Technical SEO for large sites",
      "Content strategy at scale",
      "International SEO frameworks",
      "Measurement & attribution"
    ]
  },
  {
    id: "paid-media-roi",
    title: "Maximizing Paid Media ROI in 2025",
    description: "Cut through the noise with data-backed strategies for Google Ads, LinkedIn, and Meta advertising that actually convert.",
    category: "Paid Advertising",
    readTime: "12 min read",
    downloadUrl: "#",
    highlights: [
      "Platform-specific strategies",
      "Budget allocation models",
      "Creative testing frameworks",
      "Attribution modeling"
    ]
  },
  {
    id: "digital-transformation",
    title: "Digital Transformation for Traditional Businesses",
    description: "A practical roadmap for established companies looking to modernize their marketing and sales operations.",
    category: "Strategy",
    readTime: "18 min read",
    downloadUrl: "#",
    highlights: [
      "Technology stack selection",
      "Change management tactics",
      "Integration best practices",
      "Success metrics & KPIs"
    ]
  }
];

const WhitePapers = () => {
  const { toast } = useToast();
  const [selectedPaper, setSelectedPaper] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: "", email: "", company: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDownload = async (paperId: string) => {
    if (!formData.name || !formData.email) {
      toast({
        title: "Required fields",
        description: "Please enter your name and email to download.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Store lead in Supabase
      const { error } = await supabase.from("form_submissions").insert({
        form_type: "white_paper_download",
        name: formData.name,
        email: formData.email,
        company_name: formData.company,
        additional_data: { paper_id: paperId, paper_title: whitePapers.find(p => p.id === paperId)?.title }
      });

      if (error) throw error;

      toast({
        title: "Download ready!",
        description: "Check your email for the download link."
      });

      setSelectedPaper(null);
      setFormData({ name: "", email: "", company: "" });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <EnhancedSEO
        type="resources"
        title="White Papers & Guides | Vincent Growth Partners"
        description="Download free B2B marketing white papers, lead generation guides, and SEO playbooks. Expert insights for scaling businesses."
        keywords="B2B white papers, marketing guides, lead generation playbook, SEO guide, business growth resources"
        url="/white-papers"
      />

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="py-16 bg-gradient-to-br from-background via-secondary/5 to-accent/5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <Badge variant="secondary" className="mb-4">Free Resources</Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                White Papers & Guides
              </h1>
              <p className="text-xl text-muted-foreground">
                Deep-dive resources on B2B marketing, lead generation, and growth strategies. 
                Actionable insights backed by real data.
              </p>
            </div>
          </div>
        </section>

        {/* White Papers Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {whitePapers.map((paper) => (
                  <Card key={paper.id} className="border-border/50 hover:border-accent/30 transition-all duration-300 hover:shadow-lg">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-secondary to-accent rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-primary" />
                        </div>
                        <Badge variant="outline">{paper.category}</Badge>
                      </div>
                      <CardTitle className="text-xl text-foreground mt-4">{paper.title}</CardTitle>
                      <CardDescription className="text-base">{paper.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-6">
                        {paper.highlights.map((highlight, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                            {highlight}
                          </li>
                        ))}
                      </ul>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {paper.readTime}
                        </div>
                        <Button 
                          onClick={() => setSelectedPaper(paper.id)}
                          className="group"
                        >
                          Download Free
                          <Download className="w-4 h-4 ml-2 group-hover:translate-y-0.5 transition-transform" />
                        </Button>
                      </div>

                      {/* Download Form */}
                      {selectedPaper === paper.id && (
                        <div className="mt-6 p-4 bg-secondary/10 rounded-lg border border-border/50">
                          <p className="text-sm font-medium text-foreground mb-4">
                            Enter your details to download:
                          </p>
                          <div className="space-y-3">
                            <Input
                              placeholder="Your name *"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                            <Input
                              type="email"
                              placeholder="Work email *"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                            <Input
                              placeholder="Company (optional)"
                              value={formData.company}
                              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            />
                            <div className="flex gap-2">
                              <Button 
                                onClick={() => handleDownload(paper.id)}
                                disabled={isSubmitting}
                                className="flex-1"
                              >
                                {isSubmitting ? "Processing..." : "Get Download Link"}
                              </Button>
                              <Button 
                                variant="outline"
                                onClick={() => setSelectedPaper(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-3">
                            We respect your privacy. Unsubscribe anytime.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-4">
                Need Custom Research?
              </h2>
              <p className="text-white/90 mb-6">
                Looking for industry-specific insights? Our team can provide custom analysis and recommendations for your business.
              </p>
              <Button asChild size="lg" variant="secondary" className="group">
                <a href="/book-call">
                  Request Custom Analysis
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhitePapers;
